import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { throttle } from 'lodash';
import './FloorPlanScreen.css';
import { useRestaurantSections } from '../../data/RestaurantSections';
import Sidebar from '../../components/Sidebar/Sidebar';
import Controls from '../../components/Controls/Controls';
import Table from '../../components/Table/Table';
import Text from '../../components/Text/Text';
import Tabs from '../../components/Tabs/Tabs';
import TopBar from '../../components/TopBar/TopBar';
import AddReservationModal from '../../components/AddResrervationModal/AddReservationModal';
import AddTextModal from '../../components/AddTextModal/AddTextModal';
import { useTables, useAddTable, useUpdateTable, useDeleteTable } from '../../api/tables';
import { useTexts, useAddText, useUpdateText, useDeleteText } from '../../api/texts';
import { useReservations, useUpdateReservation, useAddReservation, useReservationsFreeTable } from '../../api/reservations';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { useRestaurant } from '../../api/restaurant';

const FloorPlanScreen = () => {
  const { data: tables = [], isLoading: tablesLoading, error: tablesError, refetch: refetchTables } = useTables({
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
  const isMobile = window.innerWidth <= 768;

  const { data: texts = [], isLoading: textsLoading, error: textsError, refetch: refetchTexts } = useTexts({
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
  const queryClient = useQueryClient();
  const { sections = [], isLoading: sectionsLoading, error: sectionsError } = useRestaurantSections();
  const { data: restaurant, isLoading: isLoadingRestaurant } = useRestaurant();
  const [activeSection, setActiveSection] = useState(null);
  const addTableMutation = useAddTable();
  const updateTableMutation = useUpdateTable();
  const deleteTableMutation = useDeleteTable();
  const addTextMutation = useAddText();
  const updateTextMutation = useUpdateText();
  const deleteTextMutation = useDeleteText();
  const { data: reservations = [], isLoading: reservationsLoading, error: reservationsError } = useReservations();
  const { data: reservationsFreeTable = [], isLoading: reservationsFreeTableLoading, error: reservationsFreeTableError } = useReservationsFreeTable();
  const updateReservationMutation = useUpdateReservation();

  const [sectionTables, setSectionTables] = useState({});
  const [sectionTexts, setSectionTexts] = useState({});
  const [tableShape, setTableShape] = useState("circle");
  const [seatCount, setSeatCount] = useState(4);
  const [draggingTable, setDraggingTable] = useState(null);
  const [draggingText, setDraggingText] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [tempSelectedTables, setTempSelectedTables] = useState([]);
  const [tempPartySize, setTempPartySize] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWalkInModalOpen, setIsWalkInModalOpen] = useState(false); // New state for walk-in modal
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [floorPlanWidth, setFloorPlanWidth] = useState(800);
  const [floorPlanHeight, setFloorPlanHeight] = useState(600);
  const floorPlanRef = useRef(null);
  const isPanning = useRef(false);
  const pinchStartDistance = useRef(null);
  const pinchStartCenter = useRef({ x: 0, y: 0 });
  const pinchStartZoom = useRef(1);

  const memoizedTables = useMemo(() => tables, [tables]);
  const memoizedTexts = useMemo(() => texts, [texts]);

  useEffect(() => {
    if (sections?.length > 0 && !activeSection) {
      setActiveSection(sections[0].id);
    }
  }, [sections, activeSection]);

  useEffect(() => {
    if (restaurant) {
      setFloorPlanHeight(restaurant.height);
      setFloorPlanWidth(restaurant.width);
    }
  }, [restaurant]);

  useEffect(() => {
    if (!sections?.length) return;

    setSectionTables(prev => {
      const initialTables = {};
      sections.forEach(section => {
        const dbTables = memoizedTables.filter(t => t.section_id === section.id) || [];
        initialTables[section.id] = [
          ...(prev[section.id] || []),
          ...dbTables.filter(t => !prev[section.id]?.some(localT => localT.id === t.id))
        ];
      });

      const allTables = sections.reduce((acc, section) => {
        const sectionTables = initialTables[section.id] || [];
        return [...acc, ...sectionTables.map(table => ({
          ...table,
          section_id: section.id
        }))];
      }, []);

      const sortedTables = allTables.sort((a, b) => 
        (a.display_number || a.id) - (b.display_number || b.id)
      );

      const updatedTables = sortedTables.map((table, index) => ({
        ...table,
        x: table.x ?? 0,
        y: table.y ?? 0,
        rotation: table.rotation ?? 0,
        display_number: index + 1,
      }));

      const merged = {};
      let hasChanges = false;
      sections.forEach(section => {
        const sectionTables = updatedTables.filter(t => t.section_id === section.id);
        if (JSON.stringify(sectionTables) !== JSON.stringify(prev[section.id] || [])) {
          merged[section.id] = sectionTables;
          hasChanges = true;
        } else {
          merged[section.id] = prev[section.id] || [];
        }
      });

      if (!hasChanges) {
        return prev;
      }

      return merged;
    });

    setSectionTexts(prev => {
      const initialTexts = {};
      sections.forEach(section => {
        const dbTexts = memoizedTexts.filter(t => t.section_id === section.id) || [];
        initialTexts[section.id] = [
          ...(prev[section.id] || []),
          ...dbTexts.filter(t => !prev[section.id]?.some(localT => localT.id === t.id))
        ];
      });

      const merged = { ...prev };
      let hasChanges = false;
      sections.forEach(section => {
        const sectionTextsSorted = initialTexts[section.id]?.sort((a, b) => a.display_number - b.display_number || a.id - b.id) || [];
        const newTexts = sectionTextsSorted.map((text, index) => {
          const localText = prev[section.id]?.find(t => t.id === text.id);
          return {
            ...text,
            x: localText?.x ?? text.x ?? 0,
            y: localText?.y ?? text.y ?? 0,
            rotation: localText?.rotation ?? text.rotation ?? 0,
            display_number: text.display_number ?? (index + 1),
          };
        });

        if (JSON.stringify(newTexts) !== JSON.stringify(prev[section.id] || [])) {
          merged[section.id] = newTexts;
          hasChanges = true;
        } else {
          merged[section.id] = prev[section.id] || [];
        }
      });

      if (!hasChanges) {
        return prev;
      }

      return merged;
    });
  }, [memoizedTables, memoizedTexts, sections]);

  useEffect(() => {
    const floorPlan = floorPlanRef.current;
    if (!floorPlan) return;

    const getPinchDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const getPinchCenter = (touches) => {
      const floorPlanRect = floorPlan.getBoundingClientRect();
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2 - floorPlanRect.left,
        y: (touches[0].clientY + touches[1].clientY) / 2 - floorPlanRect.top,
      };
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        pinchStartDistance.current = getPinchDistance(e.touches);
        pinchStartCenter.current = getPinchCenter(e.touches);
        pinchStartZoom.current = zoomLevel;
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const currentDistance = getPinchDistance(e.touches);
        const scaleFactor = currentDistance / pinchStartDistance.current;
        const newZoomLevel = Math.min(Math.max(pinchStartZoom.current * scaleFactor, 0.5), 2);
        const centerX = pinchStartCenter.current.x / zoomLevel;
        const centerY = pinchStartCenter.current.y / zoomLevel;
        const newPanX = panOffset.x + centerX * (zoomLevel - newZoomLevel);
        const newPanY = panOffset.y + centerY * (zoomLevel - newZoomLevel);
        setZoomLevel(newZoomLevel);
        setPanOffset({ x: newPanX, y: newPanY });
      }
    };

    const handleTouchEnd = () => {
      pinchStartDistance.current = null;
      pinchStartCenter.current = { x: 0, y: 0 };
      pinchStartZoom.current = 1;
    };

    floorPlan.addEventListener('touchstart', handleTouchStart);
    floorPlan.addEventListener('touchmove', handleTouchMove);
    floorPlan.addEventListener('touchend', handleTouchEnd);

    return () => {
      floorPlan.removeEventListener('touchstart', handleTouchStart);
      floorPlan.removeEventListener('touchmove', handleTouchMove);
      floorPlan.removeEventListener('touchend', handleTouchEnd);
    };
  }, [zoomLevel, panOffset]);

  const allTables = useMemo(() => Object.values(sectionTables).flat(), [sectionTables]);
  const allTexts = useMemo(() => Object.values(sectionTexts).flat(), [sectionTexts]);

  const screenToFloorPlan = (screenX, screenY) => {
    const floorPlanRect = floorPlanRef.current?.getBoundingClientRect();
    if (!floorPlanRect) return { x: 0, y: 0 };
    
    return {
      x: (screenX - floorPlanRect.left) / zoomLevel - panOffset.x,
      y: (screenY - floorPlanRect.top) / zoomLevel - panOffset.y
    };
  };

  const addTable = () => {
    if (!activeSection) {
      alert("Please wait for sections to load or select a section.");
      return;
    }
    const maxDisplayNumber = Math.max(0, ...allTables.map(t => t.display_number || 0));
    const newTable = {
      section_id: activeSection,
      shape: tableShape,
      seats: parseInt(seatCount),
      x: 50,
      y: 50,
      width: tableShape === "circle" ? 80 : 80,
      height: tableShape === "circle" ? 80 : (tableShape === "square" ? 80 : 40),
      rotation: 0,
      reservation_ids: [],
      display_number: maxDisplayNumber + 1,
    };
    addTableMutation.mutate(newTable, {
      onSuccess: (data) => {
        setSectionTables(prev => {
          const updated = {
            ...prev,
            [activeSection]: [...(prev[activeSection] || []), { ...data, display_number: newTable.display_number }]
          };
          return updated;
        });
        refetchTables();
      },
      onError: (error) => {
        console.error("Error adding table:", error);
        alert(`Error adding table: ${error.message || 'Unknown error'}`);
      },
    });
  };

  const addText = (newText) => {
    if (!activeSection) {
      alert("Please wait for sections to load or select a section.");
      return;
    }
    const textData = {
      section_id: activeSection,
      content: newText.content,
      x: 50,
      y: 50,
      width: 100,
      height: 50,
      rotation: 0,
      display_number: (sectionTexts[activeSection]?.length || 0) + 1,
    };
    addTextMutation.mutate(textData, {
      onSuccess: (data) => {
        setSectionTexts(prev => {
          const updated = {
            ...prev,
            [activeSection]: [...(prev[activeSection] || []), { ...data, display_number: textData.display_number }]
          };
          return updated;
        });
        refetchTexts();
      },
      onError: (error) => {
        console.error("Error adding text:", error);
        alert(`Error adding text: ${error.message || 'Unknown error'}`);
      },
    });
  };

  const deleteTable = (tableId) => {
    deleteTableMutation.mutate({ id: tableId }, {
      onSuccess: () => {
        setSectionTables(prev => {
          const updatedSection = prev[activeSection]?.filter(t => t.id !== tableId) || [];
          const allTables = sections.reduce((acc, section) => {
            const sectionTables = section.id === activeSection ? updatedSection : (prev[section.id] || []);
            return [...acc, ...sectionTables.map(table => ({
              ...table,
              section_id: section.id
            }))];
          }, []);

          const sortedTables = allTables.sort((a, b) => 
            (a.display_number || a.id) - (b.display_number || b.id)
          );

          const renumberedTables = sortedTables.map((table, index) => ({
            ...table,
            display_number: index + 1,
          }));

          const updated = {};
          sections.forEach(section => {
            updated[section.id] = renumberedTables.filter(t => t.section_id === section.id);
          });

          renumberedTables.forEach(table => updateTableMutation.mutate(table));
          return updated;
        });
        refetchTables();
      },
      onError: (error) => {
        console.error("Error deleting table:", error);
        alert(`Error deleting table: ${error.message || 'Unknown error'}`);
      },
    });
  };

  const deleteText = (textId) => {
    deleteTextMutation.mutate({ id: textId }, {
      onSuccess: () => {
        setSectionTexts(prev => {
          const updatedSection = prev[activeSection]?.filter(t => t.id !== textId) || [];
          const renumbered = updatedSection.map((text, index) => ({
            ...text,
            display_number: index + 1,
          }));
          const updated = { ...prev, [activeSection]: renumbered };
          renumbered.forEach(text => updateTextMutation.mutate(text));
          return updated;
        });
        refetchTexts();
      },
      onError: (error) => {
        console.error("Error deleting text:", error);
        alert(`Error deleting text: ${error.message || 'Unknown error'}`);
      },
    });
  };

  const saveLayout = () => {
    if (!activeSection) {
      alert("Please select a section to save the layout.");
      return;
    }
    const tablesToSave = sectionTables[activeSection]?.map(table => ({
      id: table.id,
      section_id: activeSection,
      shape: table.shape,
      seats: table.seats,
      x: Math.round(table.x),
      y: Math.round(table.y),
      width: Math.round(table.width),
      height: Math.round(table.height),
      rotation: Math.round(table.rotation),
      reservation_ids: table.reservation_ids,
      display_number: table.display_number,
    })) || [];
    tablesToSave.forEach(table => {
      updateTableMutation.mutate(table, {
        onSuccess: (data) => {
          refetchTables();
        },
        onError: (error) => {
          console.error("Error saving table:", error);
          alert(`Error saving layout: ${error.message || 'Unknown error'}`);
        },
      });
    });

    const textsToSave = sectionTexts[activeSection]?.map(text => ({
      id: text.id,
      section_id: activeSection,
      content: text.content,
      x: Math.round(text.x),
      y: Math.round(text.y),
      width: Math.round(text.width),
      height: Math.round(text.height),
      rotation: Math.round(text.rotation),
      display_number: text.display_number,
    })) || [];
    textsToSave.forEach(text => {
      updateTextMutation.mutate(text, {
        onSuccess: (data) => {
          refetchTexts();
        },
        onError: (error) => {
          console.error("Error saving text:", error);
          alert(`Error saving layout: ${error.message || 'Unknown error'}`);
        },
      });
    });
  };

  const toggleEditing = () => {
    if (isEditing) {
      saveLayout();
    }
    setIsEditing(!isEditing);
    setSelectedReservation(null);
    setTempSelectedTables([]);
    setTempPartySize(null);
  };

  const handleTextDragStart = useCallback((e, text) => {
    if (isPanning.current || e.touches?.length > 1) return;
    e.preventDefault();
    setDraggingText(text);
    
    const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
    
    const startPos = screenToFloorPlan(clientX, clientY);
    const dragOffset = {
      x: startPos.x - text.x,
      y: startPos.y - text.y
    };

    const onMove = throttle((moveEvent) => {
      moveEvent.preventDefault();
      const moveX = moveEvent.type === "touchmove" ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const moveY = moveEvent.type === "touchmove" ? moveEvent.touches[0].clientY : moveEvent.clientY;

      const floorPlanPos = screenToFloorPlan(moveX, moveY);
      const newX = floorPlanPos.x - dragOffset.x;
      const newY = floorPlanPos.y - dragOffset.y;

      const constrainedX = Math.max(0, Math.min(newX, floorPlanWidth - text.width));
      const constrainedY = Math.max(0, Math.min(newY, floorPlanHeight - text.height));

      setSectionTexts(prev => {
        const updated = {
          ...prev,
          [activeSection]: prev[activeSection]?.map(t =>
            t.id === text.id ? { ...t, x: constrainedX, y: constrainedY } : t
          ) || [],
        };
        return updated;
      });
    }, 16);

    const onEnd = () => {
      setDraggingText(null);
      if (draggingText) {
        const currentText = sectionTexts[activeSection]?.find(t => t.id === text.id);
        if (currentText) {
          updateTextMutation.mutate(currentText, {
            onSuccess: (data) => {
              refetchTexts();
            },
            onError: (error) => {
              console.error("Error updating text position:", error);
            },
          });
        }
      }
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };

    if (e.type === "touchstart") {
      window.addEventListener("touchmove", onMove);
      window.addEventListener("touchend", onEnd);
    } else {
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onEnd);
    }
  }, [activeSection, zoomLevel, panOffset, draggingText, sectionTexts, floorPlanWidth, floorPlanHeight]);

  const handleTextDragEnd = useCallback((textId) => {
    setDraggingText(null);
  }, []);

  const handleDragStart = useCallback((e, table) => {
    if (isPanning.current || e.touches?.length > 1) return;
    e.preventDefault();
    setDraggingTable(table);
    
    const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
    
    const startPos = screenToFloorPlan(clientX, clientY);
    const dragOffset = {
      x: startPos.x - table.x,
      y: startPos.y - table.y
    };

    const onMove = throttle((moveEvent) => {
      moveEvent.preventDefault();
      const moveX = moveEvent.type === "touchmove" ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const moveY = moveEvent.type === "touchmove" ? moveEvent.touches[0].clientY : moveEvent.clientY;

      const floorPlanPos = screenToFloorPlan(moveX, moveY);
      const newX = floorPlanPos.x - dragOffset.x;
      const newY = floorPlanPos.y - dragOffset.y;

      const constrainedX = Math.max(0, Math.min(newX, floorPlanWidth - table.width));
      const constrainedY = Math.max(0, Math.min(newY, floorPlanHeight - table.height));

      setSectionTables(prev => {
        const updated = {
          ...prev,
          [activeSection]: prev[activeSection]?.map(t =>
            t.id === table.id ? { ...t, x: constrainedX, y: constrainedY } : t
          ) || [],
        };
        return updated;
      });
    }, 16);

    const onEnd = () => {
      setDraggingTable(null);
      if (draggingTable) {
        const currentTable = sectionTables[activeSection]?.find(t => t.id === table.id);
        if (currentTable) {
          updateTableMutation.mutate(currentTable, {
            onSuccess: (data) => {
              refetchTables();
            },
            onError: (error) => {
              console.error("Error updating table position:", error);
            },
          });
        }
      }
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };

    if (e.type === "touchstart") {
      window.addEventListener("touchmove", onMove);
      window.addEventListener("touchend", onEnd);
    } else {
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onEnd);
    }
  }, [activeSection, zoomLevel, panOffset, draggingTable, sectionTables, floorPlanWidth, floorPlanHeight]);

  const handleDragEnd = useCallback((tableId) => {
    setDraggingTable(null);
  }, []);

  const handlePanStart = useCallback((e) => {
    if (e.target.classList.contains('floor-plan-area') && e.touches?.length !== 2) {
      e.preventDefault();
      isPanning.current = true;
      floorPlanRef.current.classList.add('panning');
      
      const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
      
      const startPanX = panOffset.x;
      const startPanY = panOffset.y;

      const onMove = throttle((moveEvent) => {
        moveEvent.preventDefault();
        const moveX = moveEvent.type === "touchmove" ? moveEvent.touches[0].clientX : moveEvent.clientX;
        const moveY = moveEvent.type === "touchmove" ? moveEvent.touches[0].clientY : moveEvent.clientY;
        
        const deltaX = (moveX - clientX) / zoomLevel;
        const deltaY = (moveY - clientY) / zoomLevel;
        
        setPanOffset({
          x: startPanX + deltaX,
          y: startPanY + deltaY,
        });
      }, 16);

      const onEnd = () => {
        isPanning.current = false;
        floorPlanRef.current.classList.remove('panning');
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onEnd);
        window.removeEventListener("touchmove", onMove);
        window.removeEventListener("touchend", onEnd);
      };

      if (e.type === "touchstart") {
        window.addEventListener("touchmove", onMove);
        window.addEventListener("touchend", onEnd);
      } else {
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onEnd);
      }
    }
  }, [zoomLevel, panOffset]);

  const handleRotate = (tableId) => {
    const table = sectionTables[activeSection]?.find(t => t.id === tableId);
    if (table) {
      const newRotation = (table.rotation + 45) % 360;
      setSectionTables(prev => {
        const updated = {
          ...prev,
          [activeSection]: prev[activeSection]?.map(t =>
            t.id === tableId ? { ...t, rotation: newRotation } : t
          ) || [],
        };
        return updated;
      });
      updateTableMutation.mutate({ ...table, rotation: newRotation }, {
        onSuccess: (data) => {
          refetchTables();
        },
        onError: (error) => {
          console.error("Error updating table rotation:", error);
        },
      });
    }
  };

  const handleTextRotate = (textId) => {
    const text = sectionTexts[activeSection]?.find(t => t.id === textId);
    if (text) {
      const newRotation = (text.rotation + 45) % 360;
      setSectionTexts(prev => {
        const updated = {
          ...prev,
          [activeSection]: prev[activeSection]?.map(t =>
            t.id === textId ? { ...t, rotation: newRotation } : t
          ) || [],
        };
        return updated;
      });
      updateTextMutation.mutate({ ...text, rotation: newRotation }, {
        onSuccess: (data) => {
          refetchTexts();
        },
        onError: (error) => {
          console.error("Error updating text rotation:", error);
        },
      });
    }
  };

  const handleReservationClick = (reservationId) => {
    const reservation = reservations.find(res => res.id === Number(reservationId));
    if (!reservation) {
      alert("Reservation not found.");
      return;
    }
    if (Array.isArray(reservation.tableIds) && reservation.tableIds.length > 0) {
      updateReservationMutation.mutate(
        { reservationId: Number(reservationId), tableIds: [] },
        {
          onSuccess: () => {
            setTempSelectedTables([]);
            setTempPartySize({ reservationId: Number(reservationId), partySize: reservation.partySize });
            setSelectedReservation(Number(reservationId));
          },
          onError: (error) => {
            alert(`Error clearing table assignments: ${error.message || 'Unknown error'}`);
          },
        }
      );
    } else {
      setTempSelectedTables([]);
      setTempPartySize({ reservationId: Number(reservationId), partySize: reservation.partySize });
      setSelectedReservation(Number(reservationId));
    }
  };

  const handleTableClick = (tableId) => {
    if (selectedReservation && tempPartySize) {
      const reservation = reservations.find(res => res.id === Number(selectedReservation));
      if (!reservation) {
        alert("Reservation not found.");
        return;
      }
      if (!tempSelectedTables.includes(Number(tableId))) {
        const newTempTables = [...tempSelectedTables, Number(tableId)];
        const totalSeats = newTempTables.reduce((sum, tid) => {
          const table = sectionTables[activeSection]?.find(t => t.id === Number(tid));
          return sum + (table ? table.seats : 0);
        }, 0);

        setTempSelectedTables(newTempTables);

        if (totalSeats >= tempPartySize.partySize) {
          updateReservationMutation.mutate(
            { reservationId: Number(selectedReservation), tableIds: newTempTables },
            {
              onSuccess: () => {
                setTempSelectedTables([]);
                setTempPartySize(null);
                setSelectedReservation(null);
              },
              onError: (error) => {
                alert(`Error updating table assignments: ${error.message || 'Unknown error'}`);
              },
            }
          );
        } else if (totalSeats > tempPartySize.partySize) {
          alert(`Too many seats selected! You need ${tempPartySize.partySize} seats, but you've selected ${totalSeats}.`);
          setTempSelectedTables([]);
        }
      }
    }
  };

  const handleModalSubmit = (newReservation) => {
    setIsModalOpen(false);
    setIsWalkInModalOpen(false); // Close both modals
    queryClient.invalidateQueries({ queryKey: ['reservations'] });
  };

  const handleTextModalSubmit = (newText) => {
    setIsTextModalOpen(false);
    addText(newText);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleAddWalkIn = () => {
    setIsWalkInModalOpen(true); // Open walk-in modal
  };

  if (tablesLoading || textsLoading || reservationsLoading || reservationsFreeTableLoading || sectionsLoading || isLoadingRestaurant) {
    return (
      <div style={{ display: 'flex', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ width: '300px', padding: '20px', background: '#ffffff', borderRight: '1px solid #e5e7eb' }}>
          <div style={{ height: '40px', width: '80%', background: '#e5e7eb', borderRadius: '4px', marginBottom: '16px', animation: 'pulse 1.5s infinite' }}></div>
          {[...Array(4)].map((_, index) => (
            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ width: '60%', height: '16px', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s infinite' }}></div>
              <div style={{ width: '20%', height: '16px', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s infinite' }}></div>
            </div>
          ))}
        </div>
        <div style={{ flexGrow: 1, padding: '20px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ height: '40px', width: '40%', background: '#e5e7eb', borderRadius: '4px', marginBottom: '16px', animation: 'pulse 1.5s infinite' }}></div>
          <div style={{ position: 'relative', width: `${floorPlanWidth}px`, height: `${floorPlanHeight}px`, border: '2px solid #000000', background: '#f5f5f5' }}>
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  top: `${(index * 100) % floorPlanHeight}px`,
                  left: `${(index * 150) % floorPlanWidth}px`,
                  width: '80px',
                  height: '80px',
                  background: '#e5e7eb',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s infinite',
                }}
              ></div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            {[...Array(3)].map((_, index) => (
              <div key={index} style={{ height: '36px', width: '80px', background: '#e5e7eb', borderRadius: '6px', animation: 'pulse 1.5s infinite' }}></div>
            ))}
          </div>
        </div>
        <style>
          {`
            @keyframes pulse {
              0% { opacity: 1; }
              50% { opacity: 0.6; }
              100% { opacity: 1; }
            }
          `}
        </style>
      </div>
    );
  }
  if (tablesError) {
    return <div className="error">Error loading tables: {tablesError.message}</div>;
  }
  if (textsError) {
    return <div className="error">Error loading texts: {textsError.message}</div>;
  }
  if (reservationsError) {
    return <div className="error">Error loading reservations: {reservationsError.message}</div>;
  }
  if (sectionsError) {
    return <div className="error">Error loading sections: {sectionsError.message || 'Unknown error'}</div>;
  }
  if (!sections?.length) {
    return <div className="error">No restaurant sections available</div>;
  }

  return (
    <div className="app-container">
      <div className="main-content">
        <TopBar
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onAddReservation={() => setIsModalOpen(true)}
          onAddWalkIn={handleAddWalkIn} // Add walk-in handler
        />
        <div className="content-area">
          <Sidebar
            onReservationClick={handleReservationClick}
            tables={allTables}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            tempSelectedTables={tempSelectedTables}
            tempPartySize={tempPartySize}
          />
          {isMobile ? null : (
            <div className="floor-plan" style={{ position: 'relative', zIndex: 0 }}>
              <Tabs activeSection={activeSection} setActiveSection={setActiveSection} />
              <Controls
                tableShape={tableShape}
                setTableShape={setTableShape}
                seatCount={seatCount}
                setSeatCount={setSeatCount}
                onAddTable={addTable}
                isEditing={isEditing}
                toggleEditing={toggleEditing}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                isSectionsLoaded={sections?.length > 0}
                onAddText={() => setIsTextModalOpen(true)}
              />
              <div
                className="floor-plan-container"
                ref={floorPlanRef}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  height: '100%',
                  cursor: isPanning.current ? 'grabbing' : 'grab'
                }}
                onMouseDown={handlePanStart}
                onTouchStart={handlePanStart}
              >
                <div
                  className="floor-plan-area"
                  style={{
                    transform: `scale(${zoomLevel}) translate(${panOffset.x}px, ${panOffset.y}px)`,
                    transformOrigin: 'top left',
                    position: 'relative',
                    touchAction: 'none',
                    width: `${floorPlanWidth}px`,
                    height: `${floorPlanHeight}px`,
                    backgroundImage: 'url("your-background-image.jpg")',
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  {(sectionTables[activeSection] || []).map((table, index) => (
                    <Table
                      key={table.id}
                      table={{
                        ...table,
                        x: table.x,
                        y: table.y,
                        width: table.width,
                        height: table.height,
                      }}
                      displayNumber={table.display_number}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      onTableClick={handleTableClick}
                      onRotate={handleRotate}
                      onDelete={deleteTable}
                      isEditing={isEditing}
                      selectedReservation={selectedReservation}
                      selectedDate={selectedDate}
                      tempSelectedTables={tempSelectedTables}
                    />
                  ))}
                  {(sectionTexts[activeSection] || []).map((text, index) => (
                    <Text
                      key={text.id}
                      text={{
                        ...text,
                        x: text.x,
                        y: text.y,
                        width: text.width,
                        height: text.height,
                      }}
                      displayNumber={text.display_number}
                      onDragStart={handleTextDragStart}
                      onDragEnd={handleTextDragEnd}
                      onRotate={handleTextRotate}
                      onDelete={deleteText}
                      isEditing={isEditing}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <AddReservationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleModalSubmit}
        selectedDate={selectedDate}
        isWalkIn={false}
      />
      <AddReservationModal
        isOpen={isWalkInModalOpen}
        onClose={() => setIsWalkInModalOpen(false)}
        onSubmit={handleModalSubmit}
        selectedDate={selectedDate}
        isWalkIn={true}
      />
      <AddTextModal
        isOpen={isTextModalOpen}
        onClose={() => setIsTextModalOpen(false)}
        onSubmit={handleTextModalSubmit}
      />
    </div>
  );
};

export default FloorPlanScreen;